import  { useEffect, useState, useMemo } from 'react';
import { FileDown, X, File, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import TokenChecker from './TokenChecker';

const FilePreviewCard = ({ 
  // Task props
  taskId, 
  title, 
  dueDate, 
  priority, 
  assignedUsers, 
  invitedUsers,
  
  // Folder props
  folderId, 
  folderName, 
  folderColor,
  
  // Common
  onClose,
}) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const isFolder = !!folderId;
  const isTask   = !!taskId;

  // Choose endpoints
  const previewEndpoint = useMemo(() => {
    if (isFolder) return `/api/folders/${folderId}/preview-basefile/`;
    if (isTask)   return `/api/preview/${taskId}/`;
    return null;
  }, [isFolder, isTask, folderId, taskId]);

  const downloadEndpoint = useMemo(() => {
    if (isFolder) return `/api/folders/${folderId}/download-basefile/`;
    if (isTask)   return `/api/download/presign/${taskId}/`;
    return null;
  }, [isFolder, isTask, folderId, taskId]);

  // Normalize backend payloads to a common shape
  const normalize = (raw) => {
    if (!raw || typeof raw !== 'object') return null;

    const contentType =
      raw.content_type || raw.contentType || raw.mime_type || raw.mimeType || null;

    const size =
      raw.size ?? raw.content_length ?? raw.bytes ?? 0;

    const url =
      raw.url || raw.preview_url || raw.previewUrl || null;

    const filename =
      raw.filename || raw.name || raw.file_name || 'file';

    // If backend gives a boolean, honor it; else infer from type+url
    const canPreview =
      (typeof raw.can_preview === 'boolean' && raw.can_preview) ||
      raw.previewable ||
      raw.is_image ||
      raw.is_pdf ||
      raw.is_text ||
      (!!url && !!contentType && (
        contentType.startsWith('image/') ||
        contentType === 'application/pdf' ||
        contentType.startsWith('text/') ||
        contentType.includes('html')
      ));

    return { filename, content_type: contentType, size, url, can_preview: !!canPreview };
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      if (!previewEndpoint) {
        setLoading(false);
        setError('Either taskId or folderId must be provided');
        return;
      }

      try {
        const res  = await TokenChecker(previewEndpoint);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to preview file');
        const norm = normalize(data);
        if (!norm)   throw new Error('Invalid preview payload');
        if (!cancelled) setFileInfo(norm);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [previewEndpoint]);

  const handleDownload = async () => {
    try {
      if (!downloadEndpoint) throw new Error('No download endpoint');
      const res  = await TokenChecker(downloadEndpoint);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to download');
      if (!data?.url) throw new Error('No download URL returned');
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert(e.message || 'Download failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes <= 0) return '0 KB';
    const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return `${Math.round((bytes / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
  };

  const getFileIcon = (contentType, filename) => {
    const t = contentType || '';
    if (t.startsWith('image/')) return '🖼️';
    if (t.includes('pdf'))      return '📄';
    if (t.includes('word')  || /\.docx?$/.test(filename || ''))  return '📝';
    if (t.includes('excel') || /\.(xlsx?|csv)$/.test(filename || '')) return '📊';
    if (t.includes('powerpoint') || /\.pptx?$/.test(filename || ''))  return '📋';
    if (t.startsWith('text/'))  return '📃';
    return '📁';
  };

  const getPriorityColor = (p) => {
    switch ((p || '').toLowerCase()) {
      case 'immediate':   return '#EF4444';
      case 'moderate': return '#F59E0B';
      case 'lenient':    return '#10B981';
      default:       return '#6B7280';
    }
  };

  const borderColor = isFolder
    ? (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(folderColor || '') ? folderColor : '#4299E1')
    : getPriorityColor(priority);

 
  // Decide if previewable (after normalization)
  const canPreviewFile = (fi) => !!fi?.can_preview;

  const contentTypeSafe = fileInfo?.content_type || fileInfo?.type || '';

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl overflow-hidden border-t-4"
        style={{ borderTopColor: borderColor }}
      >
        {/* Header */}
        <div className="flex justify-between items-start p-5 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full" style={{ backgroundColor: borderColor }} />
            <div>
              <h2 className="text-xl font-bold text-[#2D3748] break-words">
                {isFolder ? `${folderName} - Base File` : (title || 'Attachment')}
              </h2>

              {/* Task metadata */}
              {isTask && (
                <div className="flex items-center gap-4 mt-1">
                  {dueDate && <span className="text-sm text-gray-600">Due: {dueDate}</span>}
                  {priority && (
                    <span
                      className="text-xs px-2 py-1 rounded text-white font-medium"
                      style={{ backgroundColor: getPriorityColor(priority) }}
                    >
                      {String(priority).toUpperCase()}
                    </span>
                  )}
                </div>
              )}

              {/* File info for both types */}
              {fileInfo && (
                <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                  <span className="text-lg">
                    {getFileIcon(contentTypeSafe, fileInfo.filename)}
                  </span>
                  {fileInfo.filename}
                  {fileInfo.size > 0 && (
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                      {formatFileSize(fileInfo.size)}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 h-[70vh] overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading preview...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-red-500">
                <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">Preview not available</p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            </div>
          )}

          {fileInfo && !error && !canPreviewFile(fileInfo) && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <File className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">Preview not supported</p>
                <p className="text-sm mb-4">This file type cannot be previewed online</p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  Download to view
                </button>
              </div>
            </div>
          )}

          {fileInfo?.url && !error && canPreviewFile(fileInfo) && (
            <iframe
              src={fileInfo.url}
              title={isFolder ? 'Base File Preview' : 'Task File Preview'}
              className="w-full h-full rounded border bg-white"
              onError={() => setError('Failed to load preview')}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-5 py-3 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {fileInfo && (
              <>
                {isFolder ? (
                  <>Attached to <strong>{folderName}</strong> folder</>
                ) : (
                  <>Attachment for <strong>{title || 'Task'}</strong></>
                )}
                {contentTypeSafe && (
                  <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
                    {(contentTypeSafe.split('/')[1] || 'file').toUpperCase()}
                  </span>
                )}
                {isTask && (assignedUsers?.length || invitedUsers?.length) ? (
                  <span className="ml-2 text-xs">
                    {assignedUsers?.length ? `Assigned: ${assignedUsers.length}` : ''}
                    {assignedUsers?.length && invitedUsers?.length ? ' • ' : ''}
                    {invitedUsers?.length ? `Invited: ${invitedUsers.length}` : ''}
                  </span>
                ) : null}
              </>
            )}
          </div>

          {fileInfo && (
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 text-white px-4 py-2 rounded-md transition-colors"
              style={{ backgroundColor: borderColor }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = borderColor + 'DD'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = borderColor; }}
            >
              <FileDown className="w-4 h-4" />
              Download
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FilePreviewCard;
